@if (paginator.state$ | async; as paginatorState) {
	<div class="flex h-full min-h-0 w-full flex-col gap-4">
		<div class="flex flex-wrap items-center justify-between gap-3">
			<div>
				<h1 class="text-3xl font-bold">
					<span [translate]>Task Management</span>
				</h1>
				<p class="text-base-content/60 mt-2 text-sm">Inspect queued and completed background work, and manually kick the workers when needed.</p>
			</div>
			<div class="flex flex-wrap items-center gap-2">
				<button class="btn btn-sm btn-outline" (click)="refresh()">
					<span [translate]>Refresh</span>
				</button>
				<label class="label flex cursor-pointer items-center gap-2 px-2">
					<span class="label-text text-sm">Auto refresh</span>
					<input type="checkbox" class="toggle toggle-sm" [checked]="isAutoRefreshEnabled()" (change)="toggleAutoRefresh()" />
				</label>
				<button class="btn btn-sm btn-outline" [disabled]="isRetryingFailed()" (click)="retryFailedTasks()">
					<span [translate]>Retry failed</span>
				</button>
				<button class="btn btn-sm btn-primary" [disabled]="isLaunchingProcessing()" (click)="launchProcessing()">
					<span [translate]>Launch processing</span>
				</button>
			</div>
		</div>

		<div class="bg-base-100 border-base-200 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-sm">
			<div class="border-base-200 border-b px-4 py-3">
				<div class="text-sm font-semibold">
					<span [translate]>Enlisted task methods</span>
				</div>
				@if (enlistedTaskMethods().length > 0) {
					<div class="mt-2 flex flex-wrap gap-2">
						@for (methodName of enlistedTaskMethods(); track methodName) {
							<span class="badge badge-outline badge-sm font-mono">{{ methodName }}</span>
						}
					</div>
				} @else {
					<div class="mt-2 text-sm opacity-60">
						<span [translate]>No enlisted task methods found.</span>
					</div>
				}
			</div>
			<div class="min-h-0 flex-1 overflow-auto">
				<table class="table w-full">
					<thead class="bg-base-200/50 text-base-content/60 text-xs font-bold tracking-wider uppercase">
						<tr>
							<th>
								<div class="flex items-center gap-2">
									<span>Task</span>
									<button class="btn btn-ghost btn-xs" (click)="paginator.setOrderingBy('time_created')">↑↓</button>
								</div>
							</th>
							<th>Priority</th>
							<th>Status</th>
							<th>Progress</th>
							<th>Method</th>
							<th>Worker</th>
							<th>Started</th>
							<th>Ended</th>
							<th class="text-right">Actions</th>
						</tr>
					</thead>
					<tbody>
						@for (task of paginatorState.itemsOnCurrentPage; track task?.id) {
							@if (task) {
								<tr
									class="hover:bg-base-200/30 align-top cursor-pointer"
									[class.bg-base-200/40]="openedItems.isSelected(task)"
									[tabIndex]="0"
									(click)="toggleExpandedTask(task)"
									(keyup.enter)="toggleExpandedTask(task)"
								>
									<td class="min-w-72">
										<div class="flex flex-col gap-1">
											<div class="font-medium">{{ task.title || task.methodName || task.id }}</div>
											<div class="font-mono text-xs opacity-60">{{ task.id }}</div>
											@if (task.description) {
												<div class="text-sm opacity-70">{{ task.description }}</div>
											}
											<div class="flex flex-wrap gap-2 pt-1">
												@if (task.kind) {
													<span class="badge badge-ghost badge-sm">{{ task.kind }}</span>
												}
											</div>
										</div>
									</td>
									<td class="text-sm font-medium tabular-nums">{{ task.priority ?? '-' }}</td>
									<td>
										<div class="flex flex-col gap-1">
											<span class="badge badge-sm w-fit" [class]="getStatusTone(task)">{{ getStatusLabel(task) }}</span>
											@if (!task.completed) {
												<span class="text-xs opacity-60">{{ getHeartbeatLabel(task) }}</span>
											}
										</div>
									</td>
									<td class="min-w-44">
										<div class="flex items-center gap-3">
											<progress class="progress progress-primary w-24" [value]="getProgressValue(task)" max="100"></progress>
											<span class="text-sm tabular-nums">{{ getProgressValue(task) | number: '1.0-0' }}%</span>
										</div>
									</td>
									<td class="font-mono text-xs">{{ task.methodName || '-' }}</td>
									<td>
										<div class="text-sm">{{ task.processorKind || '-' }}</div>
										@if (task.processorId) {
											<div class="font-mono text-xs opacity-60">{{ task.processorId }}</div>
										}
									</td>
									<td class="text-sm">{{ task.startedAt ? (task.startedAt | date: 'short') : '-' }}</td>
									<td class="text-sm">{{ task.endedAt ? (task.endedAt | date: 'short') : '-' }}</td>
									<td class="text-right">
										<div class="flex justify-end gap-2">
											<button class="btn btn-ghost btn-xs" (click)="$event.stopImmediatePropagation(); copyTaskId(task)">Copy id</button>
											<button class="btn btn-ghost btn-xs" [disabled]="!canRunTaskActions(task) || activeTaskActionId()" (click)="$event.stopImmediatePropagation(); recreateTask(task)">
												@if (isTaskActionPending(task, 'recreate')) {
													<span class="loading loading-spinner loading-xs"></span>
												} @else {
													<span>Recreate</span>
												}
											</button>
											<button class="btn btn-ghost btn-xs text-error" [disabled]="!canRunTaskActions(task) || activeTaskActionId()" (click)="$event.stopImmediatePropagation(); deleteTask(task)">
												@if (isTaskActionPending(task, 'delete')) {
													<span class="loading loading-spinner loading-xs"></span>
												} @else {
													<span>Delete</span>
												}
											</button>
										</div>
									</td>
								</tr>

								@if (openedItems.isSelected(task)) {
									<tr>
										<td colspan="9" class="p-0">
											<div class="bg-base-200/40 border-base-200 border-t p-4">
												<div class="mb-3 flex items-center justify-between gap-3">
													<div class="text-sm font-medium">Artifacts</div>
													<button class="btn btn-ghost btn-xs" (click)="openedItems.unselect(task)">Close</button>
												</div>
												@if (hasArtifacts(task)) {
													<pre class="bg-base-100 border-base-200 max-h-96 overflow-auto rounded-xl border p-3 text-xs whitespace-pre-wrap">{{ task.artifacts | json }}</pre>
												} @else {
													<div class="text-sm opacity-60">No artifacts available.</div>
												}
											</div>
										</td>
									</tr>
								}
							}
						} @empty {
							<tr>
								<td colspan="9" class="py-10 text-center text-sm opacity-60">No tasks found.</td>
							</tr>
						}
					</tbody>
				</table>
			</div>

			@if (paginatorState.totalNumberOfPages > 1) {
				<div class="border-base-200 flex items-center justify-between border-t px-4 py-3">
					<button class="btn btn-sm btn-ghost" [disabled]="!paginatorState.hasPrev" (click)="paginator.previousPage()">
						<span [translate]>Previous</span>
					</button>
					<div class="text-sm opacity-70">
						Page {{ paginatorState.currentPage }} / {{ paginatorState.totalNumberOfPages }} · {{ paginatorState.totalNumberOfItems }} tasks
					</div>
					<button class="btn btn-sm btn-ghost" [disabled]="!paginatorState.hasNext" (click)="paginator.nextPage()">
						<span [translate]>Next</span>
					</button>
				</div>
			}
		</div>
	</div>
}
